import { useState, type FormEvent } from 'react';
import { z } from 'zod';
import { Input, Textarea as _TextareaUnused, Select, Field } from '@/components/ui/Field'; void _TextareaUnused;
import { Checkbox } from '@/components/ui/Checkbox';
import { Button } from '@/components/ui/Button';
import { MediaField } from '@/components/forms/MediaField';
import { MarkdownField } from '@/components/forms/MarkdownField';
import { slugify } from '@/lib/utils';
import type { ProjectRow } from '@/lib/admin-queries';
import { useProjectCategories } from '@/lib/admin-queries';

const STATUSES = ['shipped', 'ongoing', 'concept'] as const;
const PUBLISH = ['draft', 'published', 'archived'] as const;

interface Props {
  value: ProjectRow;
  submitting: boolean;
  onSubmit: (v: ProjectRow) => void | Promise<void>;
  onCancel: () => void;
}

const projectSchema = z.object({
  slug: z.string().regex(/^[a-z0-9-]+$/, 'Lowercase letters, numbers, and hyphens only.'),
  title: z.string().min(2),
  category: z.string().min(1, 'Category is required'),
  status: z.enum(STATUSES),
  status_publish: z.enum(PUBLISH),
});

export function ProjectEditForm({ value, submitting, onSubmit, onCancel }: Props) {
  const { data: categories = [] } = useProjectCategories();
  const [v, setV] = useState<ProjectRow>(value);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [techInput, setTechInput] = useState('');
  const [impactInput, setImpactInput] = useState({ value: '', label: '', sublabel: '' });

  function patch<K extends keyof ProjectRow>(key: K, val: ProjectRow[K]) {
    setV((cur) => ({ ...cur, [key]: val }));
  }

  function onTitleBlur() {
    if (!v.slug || v.slug === slugify(v.title).slice(0, -1)) {
      patch('slug', slugify(v.title));
    } else if (v.slug === '') {
      patch('slug', slugify(v.title));
    }
  }

  function addTech() {
    const t = techInput.trim();
    if (!t) return;
    if (!v.tech_stack.includes(t)) patch('tech_stack', [...v.tech_stack, t]);
    setTechInput('');
  }

  function removeTech(t: string) {
    patch('tech_stack', v.tech_stack.filter((x) => x !== t));
  }

  function addImpact() {
    if (!impactInput.value.trim() || !impactInput.label.trim()) return;
    patch('business_impact', [...v.business_impact, { ...impactInput }]);
    setImpactInput({ value: '', label: '', sublabel: '' });
  }

  function removeImpact(idx: number) {
    patch('business_impact', v.business_impact.filter((_, i) => i !== idx));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});
    const parsed = projectSchema.safeParse(v);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === 'string' && !next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }
    onSubmit(v);
  }

  return (
    <form id="project-form" onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <Field id="title" label="Title" required error={errors.title}>
          {({ id, invalid }) => (
            <Input
              id={id}
              required
              invalid={invalid}
              value={v.title}
              onChange={(e) => patch('title', e.target.value)}
              onBlur={onTitleBlur}
            />
          )}
        </Field>
        <Field id="slug" label="Slug" required error={errors.slug} hint="Lowercase letters, numbers, hyphens.">
          {({ id, invalid }) => (
            <Input
              id={id}
              required
              invalid={invalid}
              value={v.slug}
              onChange={(e) => patch('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            />
          )}
        </Field>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Field id="category" label="Category" required>
          {({ id }) => (
            <Select id={id} value={v.category} onChange={(e) => patch('category', e.target.value as ProjectRow['category'])}>
              <option value="">Select a category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </Select>
          )}
        </Field>
        <Field id="status" label="Status" required>
          {({ id }) => (
            <Select id={id} value={v.status} onChange={(e) => patch('status', e.target.value as ProjectRow['status'])}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          )}
        </Field>
        <Field id="status_publish" label="Publish" required>
          {({ id }) => (
            <Select id={id} value={v.status_publish} onChange={(e) => patch('status_publish', e.target.value as ProjectRow['status_publish'])}>
              {PUBLISH.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          )}
        </Field>
        <Field id="year" label="Year">
          {({ id }) => (
            <Input id={id} type="number" min={2000} max={2100} value={v.year ?? ''} onChange={(e) => patch('year', e.target.value ? Number(e.target.value) : null)} />
          )}
        </Field>
      </div>

      <Field id="client" label="Client">
        {({ id }) => (
          <Input id={id} value={v.client ?? ''} onChange={(e) => patch('client', e.target.value)} />
        )}
      </Field>

      <Field id="summary" label="Summary" hint="≤ 160 chars · used in cards and meta description.">
        {({ id }) => (
          <Input
            id={id}
            maxLength={200}
            value={v.summary ?? ''}
            onChange={(e) => patch('summary', e.target.value)}
          />
        )}
      </Field>

      <Field id="hero" label="Hero image" hint="16:9 image · shown in cards and the detail page hero.">
        <MediaField value={v.hero_media_id} onChange={(m) => patch('hero_media_id', m)} bucket="public" />
      </Field>

      <Field id="problem" label="Problem" hint="What we set out to solve.">
        <MarkdownField value={v.problem ?? ''} onChange={(s) => patch('problem', s)} rows={8} />
      </Field>

      <Field id="approach" label="Approach" hint="How we tackled it.">
        <MarkdownField value={v.approach ?? ''} onChange={(s) => patch('approach', s)} rows={8} />
      </Field>

      <Field id="description_md" label="Full story" hint="The detailed case study body.">
        <MarkdownField value={v.description_md ?? ''} onChange={(s) => patch('description_md', s)} rows={20} />
      </Field>

      {/* TECH STACK */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-fg-muted">Tech stack</label>
        <div className="flex flex-wrap gap-1.5">
          {v.tech_stack.map((t) => (
            <button
              type="button"
              key={t}
              onClick={() => removeTech(t)}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-elevated px-2 py-1 text-xs text-fg hover:border-danger/40 hover:text-danger"
            >
              {t} <span aria-hidden>×</span>
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Add technology (e.g. React)"
            value={techInput}
            onChange={(e) => setTechInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTech();
              }
            }}
          />
          <Button type="button" variant="secondary" onClick={addTech}>
            Add
          </Button>
        </div>
      </div>

      {/* BUSINESS IMPACT */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-fg-muted">Business impact</label>
        <div className="space-y-2">
          {v.business_impact.map((m, idx) => (
            <div key={`${m.label}-${idx}`} className="flex items-center justify-between gap-2 rounded-md border border-border bg-bg-elevated px-3 py-2">
              <div>
                <p className="text-sm">
                  <span className="font-semibold text-gold-500">{m.value}</span> · {m.label}
                  {m.sublabel && <span className="text-fg-subtle"> · {m.sublabel}</span>}
                </p>
              </div>
              <button
                type="button"
                onClick={() => removeImpact(idx)}
                className="text-xs text-fg-muted hover:text-danger"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
        <div className="grid gap-2 md:grid-cols-3">
          <Input
            placeholder="Value (e.g. 3×)"
            value={impactInput.value}
            onChange={(e) => setImpactInput((s) => ({ ...s, value: e.target.value }))}
          />
          <Input
            placeholder="Label"
            value={impactInput.label}
            onChange={(e) => setImpactInput((s) => ({ ...s, label: e.target.value }))}
          />
          <Input
            placeholder="Sublabel (optional)"
            value={impactInput.sublabel}
            onChange={(e) => setImpactInput((s) => ({ ...s, sublabel: e.target.value }))}
          />
        </div>
        <Button type="button" variant="secondary" onClick={addImpact}>
          Add impact metric
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Field id="cta_label" label="CTA label" hint="Optional · shown on detail page.">
          {({ id }) => (
            <Input id={id} value={v.cta_label ?? ''} onChange={(e) => patch('cta_label', e.target.value)} />
          )}
        </Field>
        <Field id="cta_href" label="CTA URL">
          {({ id }) => (
            <Input id={id} value={v.cta_href ?? ''} onChange={(e) => patch('cta_href', e.target.value)} />
          )}
        </Field>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Field id="display_order" label="Order">
          {({ id }) => (
            <Input
              id={id}
              type="number"
              value={v.display_order}
              onChange={(e) => patch('display_order', Number(e.target.value))}
            />
          )}
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={v.is_featured}
            onCheckedChange={(c) => patch('is_featured', !!c)}
            id="is_featured"
          />
          <span>Featured on home page</span>
        </label>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" form="project-form" loading={submitting}>
          {v.id ? 'Save changes' : 'Create project'}
        </Button>
      </div>
    </form>
  );
}